// pages/Channels/Whatsapp/Whatsapp.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import axiosInstance from "../../../../utils/axiosInstance";
import { hasPermission } from "../../../../utils/permissionUtils";
import { Box, Typography, Alert } from '@mui/material';

function WhatsappChannel() {
    const { chatbotId: paramsChatbotId } = useParams();
    
    const [formData, setFormData] = useState({
        WHATSAPP_VERIFY_TOKEN: '',
        WHATSAPP_ACCESS_TOKEN: '',
        WHATSAPP_PHONE_NUMBER_ID: '',
        WHATSAPP_NUMBER: '',
        WHATSAPP_BUSINESS_ACCOUNT_ID: ''
    });
    const navigate = useNavigate();

    useEffect(() => {
        document.title = 'WhatsApp | KlonIT Web Application';        
    }, []);
    // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    const [responseMessage, setResponseMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const callbackURL = process.env.REACT_APP_BACKEND_URL_DEVELOPMENT + "/wa-webhook";
    const verifyToken = "KLONIT_VERIFY_TOKEN";

    // Check permissions
    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('channels', 'view');
                const modifyPerm = await hasPermission('channels', 'add|edit');
                const deletePerm = await hasPermission('channels', 'delete');

                setCanView(viewPerm);
                setCanModify(modifyPerm);
                setCanDelete(deletePerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanView(false);
                setCanModify(false);
                setCanDelete(false);
            } finally {
                setCheckingPermissions(false);
            }
        };

        checkPermissions();
    }, []);

    useEffect(() => {
        document.title = 'WhatsApp | KlonIT Web Application';
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setResponseMessage('');
        setErrorMessage('');

        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.post(`/add-whatsapp`, formData, { params });

            if (response.status >= 200 && response.status < 300) {
                setResponseMessage(response.data.message);
                fetchDataExists();
            } else {
                setErrorMessage(response.data.message || `Error: ${response.status}`);
            }
        } catch (error) {
            setErrorMessage(error.response.data.message || 'Failed to connect to server.');
            console.error("Error submitting WhatsApp data:", error);
        }
    };

    const fetchDataExists = async () => {
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get(`/get-whatsapp`, { params });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
            
            console.log(responseData);
            
            if (Object.keys(responseData).length > 0) {
                setFormData({
                    WHATSAPP_VERIFY_TOKEN: responseData.whatsapp_verify_token || '',
                    WHATSAPP_ACCESS_TOKEN: responseData.whatsapp_access_token || '',
                    WHATSAPP_PHONE_NUMBER_ID: responseData.whatsapp_phone_number_id || '',
                    WHATSAPP_NUMBER: responseData.whatsapp_phone_number || '',
                    WHATSAPP_BUSINESS_ACCOUNT_ID: responseData.whatsapp_business_account_id || ''
                });
            } else {
                setFormData({
                    WHATSAPP_VERIFY_TOKEN: '',
                    WHATSAPP_ACCESS_TOKEN: '',
                    WHATSAPP_PHONE_NUMBER_ID: '',
                    WHATSAPP_NUMBER: '',
                    WHATSAPP_BUSINESS_ACCOUNT_ID: ''
                });
            }
        } catch (error) {
            console.error("Error fetching WhatsApp data:", error);
            setErrorMessage('Error fetching WhatsApp data.');
        }
    };

    useEffect(() => {
        if (canView) {
            fetchDataExists();
        }
    }, [canView]);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                alert('Copied to clipboard!');
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
                alert('Failed to copy text. Please try again.');
            });
    };

    // Show loading state while checking permissions
    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <Typography>Checking permissions...</Typography>
            </Box>
        );
    }

    // Show access denied if no view permission
    if (!canView) {
        return (
            <div>
                <main className="content">
                    <div className="container-fluid p-0">
                        <Alert severity="error" sx={{ mt: 6 }}>
                            <strong>Access Denied</strong>
                            <br />
                            You don't have permission to view WhatsApp settings. Please contact your administrator.
                        </Alert>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div>
            <main className="content">
                <div className="container-fluid p-0">
                    <div className="mb-3">
                        <h1 className="h3 d-inline align-middle"> <strong>Connect </strong> WhatsApp</h1>
                    </div>
                    <div className="row">
                        <div className="col-12 col-lg-12">
                            <div className="card">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">WhatsApp Integration Setup</h5>
                                </div>
                                <div className="card-body">
                                    <div className='row'>
                                        <div className='col-md-8'>
                                            <form onSubmit={handleSubmit}>
                                                <div>
                                                    <label htmlFor="WHATSAPP_ACCESS_TOKEN">WhatsApp Access Token:</label>
                                                    <input
                                                        type="text"
                                                        id="WHATSAPP_ACCESS_TOKEN"
                                                        name="WHATSAPP_ACCESS_TOKEN"
                                                        value={formData.WHATSAPP_ACCESS_TOKEN}
                                                        onChange={handleChange}
                                                        required
                                                        placeholder='WhatsApp Access Token'
                                                        disabled={!canModify}
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor="WHATSAPP_PHONE_NUMBER_ID">WhatsApp Phone Number ID:</label>
                                                    <input
                                                        type="text"
                                                        id="WHATSAPP_PHONE_NUMBER_ID"
                                                        name="WHATSAPP_PHONE_NUMBER_ID"
                                                        value={formData.WHATSAPP_PHONE_NUMBER_ID}
                                                        onChange={handleChange}
                                                        required
                                                        placeholder="WhatsApp Phone Number ID"
                                                        disabled={!canModify}
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor="WHATSAPP_BUSINESS_ACCOUNT_ID">WhatsApp Business Account ID:</label>
                                                    <input
                                                        type="text"
                                                        id="WHATSAPP_BUSINESS_ACCOUNT_ID"
                                                        name="WHATSAPP_BUSINESS_ACCOUNT_ID"
                                                        value={formData.WHATSAPP_BUSINESS_ACCOUNT_ID}
                                                        onChange={handleChange}
                                                        required
                                                        placeholder='WhatsApp Business Account ID'
                                                        disabled={!canModify}
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor="WHATSAPP_NUMBER">WhatsApp Number:</label>
                                                    <input
                                                        type="text"
                                                        id="WHATSAPP_NUMBER"
                                                        name="WHATSAPP_NUMBER"
                                                        value={formData.WHATSAPP_NUMBER}
                                                        onChange={handleChange}
                                                        required
                                                        placeholder='WhatsApp number with country code'
                                                        disabled={!canModify}
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor="WHATSAPP_VERIFY_TOKEN">WhatsApp Verify Token:</label>
                                                    <input
                                                        type="text"
                                                        id="WHATSAPP_VERIFY_TOKEN"
                                                        name="WHATSAPP_VERIFY_TOKEN"
                                                        value={formData.WHATSAPP_VERIFY_TOKEN}
                                                        onChange={handleChange}
                                                        required
                                                        placeholder='WhatsApp Verify Token'
                                                        disabled={!canModify}
                                                    />
                                                </div>
                                                {canModify && (
                                                    <button className='btn btn-primary' type="submit">
                                                        Connect with WhatsApp
                                                    </button>
                                                )}
                                            </form>
                                        </div>
                                        <div className='col-md-4'>
                                            <h3>Callback URL:</h3>
                                            <p className='copy-callback'>
                                                {callbackURL}
                                                <i className="fa fa-copy copy-icon" onClick={() => copyToClipboard(callbackURL)} aria-hidden="true"></i>
                                            </p>
                                            <br/>
                                            <h3>Verify token:</h3>
                                            <p className='copy-token'>
                                                {verifyToken}
                                                <i className="fa fa-copy copy-icon" onClick={() => copyToClipboard(verifyToken)} aria-hidden="true"></i>
                                            </p>
                                        </div>
                                    </div>

                                    <br />
                                    {responseMessage && <div className="alert alert-info">{responseMessage}</div>}
                                    {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default WhatsappChannel;